import { Navigate, useLocation } from "react-router-dom";

import { useRedux } from "@/hooks/useRedux";

import { UserRole } from "@/types";

import { useKeycloakAuth } from "@/contexts/KeycloakAuthContext";
import { ONBOARDING_ROLES, Routes } from "@/constants/settings";

export const PrivateRoute = ({
  children,
  acceptedRoles,
}: {
  children: React.ReactElement;
  acceptedRoles?: UserRole[];
}) => {
  const { userAccount } = useRedux("userAccount");
  const { isKeycloakMode } = useKeycloakAuth();
  const location = useLocation();

  const isRoleRestricted = acceptedRoles && acceptedRoles?.length > 0;
  const isRoleAllowed = userAccount.role && acceptedRoles?.includes(userAccount.role as UserRole);

  const renderContent = () => {
    // Force redirect to onboarding if tenant has not completed onboarding,
    // regardless of which protected page the user navigates to.
    if (
      userAccount.isFirstLogin &&
      userAccount.role &&
      ONBOARDING_ROLES.includes(userAccount.role as UserRole) &&
      location.pathname !== Routes.ONBOARDING
    ) {
      return <Navigate to={Routes.ONBOARDING} replace />;
    }

    // Block access to onboarding page if onboarding is already completed.
    if (!userAccount.isFirstLogin && location.pathname === Routes.ONBOARDING) {
      return <Navigate to={Routes.HOME} replace />;
    }

    if (!isRoleRestricted || isRoleAllowed) {
      return children;
    }

    return (
      <Navigate
        to={{
          pathname: "/unauthorized",
        }}
      />
    );
  };

  // In Keycloak mode, if not authenticated yet, show a loading indicator.
  // The KeycloakUserSession component handles reloading the page on 401.
  if (isKeycloakMode) {
    if (!userAccount.isAuthenticated) {
      return <div className="KeycloakLoading">Redirecting to login…</div>;
    }
    return renderContent();
  }

  // Legacy mode: redirect to sign-in page if not authenticated
  return !userAccount.isAuthenticated || userAccount.isSessionExpired ? (
    <Navigate
      to={{
        pathname: "/",
        search: location.search,
      }}
    />
  ) : (
    renderContent()
  );
};
